/**
 * CreatePaymentModal - Create new payment with optional bill/invoice pre-linking
 * 
 * Features:
 * - Payment type selection (expense/sales)
 * - Vendor selection for expense payments
 * - Customer selection for sales payments
 * - Bill linking for expense payments
 * - Invoice linking for sales payments
 * - Pre-fill support when opened from Bill or Invoice detail
 */

import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  Loader2, 
  DollarSign, 
  Building2, 
  CreditCard,
  CheckCircle,
  FileText,
  User,
} from 'lucide-react';
import { paymentsAPI, vendorsAPI, vendorBillsAPI, crmAPI } from '../../utils/api';

// Payment methods
const PAYMENT_METHODS = [
  { value: 'check', label: 'Check' },
  { value: 'ach', label: 'ACH / Bank Transfer' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'wire', label: 'Wire Transfer' },
  { value: 'cash', label: 'Cash' },
  { value: 'other', label: 'Other' },
];

// Format currency
const formatCurrency = (cents) => {
  if (cents === null || cents === undefined) return '$0.00';
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const CreatePaymentModal = ({ 
  open, 
  onOpenChange, 
  onSuccess,
  // Pre-fill props for expense payments
  prefillVendor = null,  // { id, name }
  prefillBill = null,    // { id, bill_number, amount_cents, balance_cents }
  // Pre-fill props for sales payments
  prefillCustomer = null, // { id, name }
  prefillInvoice = null,  // { id, invoice_number, total_cents, balance_cents }
  defaultType = 'expense', // 'expense' or 'sales'
}) => {
  const [formData, setFormData] = useState({
    payment_type: defaultType,
    vendor_id: '',
    vendor_name: '',
    customer_id: '',
    customer_name: '',
    amount: '',
    payment_method: 'check',
    reference_number: '',
    payment_date: new Date().toISOString().split('T')[0],
    memo: '',
  });
  
  // Vendor state (expense payments)
  const [vendors, setVendors] = useState([]);
  const [vendorSearch, setVendorSearch] = useState('');
  const [vendorSearchResults, setVendorSearchResults] = useState([]);
  const [selectedBills, setSelectedBills] = useState([]);
  const [availableBills, setAvailableBills] = useState([]);
  const [loadingBills, setLoadingBills] = useState(false);
  
  // Customer state (sales payments)
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [availableInvoices, setAvailableInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  
  const [submitting, setSubmitting] = useState(false);

  // Fetch vendors on open (expense mode)
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const res = await vendorsAPI.getVendors({ page_size: 100 });
        setVendors(res.vendors || []);
      } catch (err) {
        console.error('Failed to fetch vendors:', err);
      }
    };
    if (open && formData.payment_type === 'expense') fetchVendors();
  }, [open, formData.payment_type]);

  // Fetch customers on open (sales mode)
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const res = await crmAPI.getAccounts({ page_size: 200 });
        setCustomers(res.accounts || []);
      } catch (err) {
        console.error('Failed to fetch customers:', err);
      }
    };
    if (open && formData.payment_type === 'sales') fetchCustomers();
  }, [open, formData.payment_type]);

  // Search vendors
  useEffect(() => {
    if (vendorSearch.length >= 2) {
      const results = vendors.filter(v => 
        v.name.toLowerCase().includes(vendorSearch.toLowerCase())
      );
      setVendorSearchResults(results.slice(0, 10));
    } else {
      setVendorSearchResults([]);
    }
  }, [vendorSearch, vendors]);

  // Search customers
  useEffect(() => {
    if (customerSearch.length >= 2) {
      const results = customers.filter(c => 
        c.name.toLowerCase().includes(customerSearch.toLowerCase())
      );
      setCustomerSearchResults(results.slice(0, 10));
    } else {
      setCustomerSearchResults([]);
    }
  }, [customerSearch, customers]);

  // Fetch unpaid bills when vendor is selected
  useEffect(() => {
    const fetchBills = async () => {
      if (!formData.vendor_id || formData.payment_type !== 'expense') {
        setAvailableBills([]);
        return;
      }
      
      setLoadingBills(true);
      try {
        const res = await vendorBillsAPI.getBills({ 
          vendor_id: formData.vendor_id, 
          status: 'unpaid',
          page_size: 50 
        });
        // Also fetch partial bills
        const partialRes = await vendorBillsAPI.getBills({
          vendor_id: formData.vendor_id,
          status: 'partial',
          page_size: 50
        });
        const allBills = [...(res.bills || []), ...(partialRes.bills || [])];
        setAvailableBills(allBills);
      } catch (err) {
        console.error('Failed to fetch bills:', err);
      } finally {
        setLoadingBills(false);
      }
    };
    
    fetchBills();
  }, [formData.vendor_id, formData.payment_type]);

  // Fetch open invoices when customer is selected
  useEffect(() => {
    const fetchInvoices = async () => {
      if (!formData.customer_id || formData.payment_type !== 'sales') {
        setAvailableInvoices([]);
        return;
      }
      
      setLoadingInvoices(true);
      try {
        const res = await paymentsAPI.getCustomerOpenInvoices(formData.customer_id);
        setAvailableInvoices(res.invoices || []);
      } catch (err) {
        console.error('Failed to fetch invoices:', err);
        setAvailableInvoices([]);
      } finally {
        setLoadingInvoices(false);
      }
    };
    
    fetchInvoices();
  }, [formData.customer_id, formData.payment_type]);

  // Initialize form with prefill data
  useEffect(() => {
    if (open) {
      // Handle expense prefill
      if (prefillVendor?.id) {
        setFormData(prev => ({
          ...prev,
          payment_type: 'expense',
          vendor_id: prefillVendor.id,
          vendor_name: prefillVendor.name || '',
        }));
      }
      if (prefillBill?.id) {
        setSelectedBills([{
          id: prefillBill.id,
          bill_number: prefillBill.bill_number,
          amount_cents: prefillBill.amount_cents,
          balance_cents: prefillBill.balance_cents,
          apply_amount: (prefillBill.balance_cents / 100).toFixed(2),
        }]);
        setFormData(prev => ({
          ...prev,
          amount: (prefillBill.balance_cents / 100).toFixed(2),
        }));
      }
      
      // Handle sales prefill
      if (prefillCustomer?.id) {
        setFormData(prev => ({
          ...prev,
          payment_type: 'sales',
          customer_id: prefillCustomer.id,
          customer_name: prefillCustomer.name || '',
        }));
      }
      if (prefillInvoice?.id) {
        setSelectedInvoices([{
          id: prefillInvoice.id,
          invoice_number: prefillInvoice.invoice_number,
          total_cents: prefillInvoice.total_cents,
          balance_cents: prefillInvoice.balance_cents,
          apply_amount: (prefillInvoice.balance_cents / 100).toFixed(2),
        }]);
        setFormData(prev => ({
          ...prev,
          amount: (prefillInvoice.balance_cents / 100).toFixed(2),
        }));
      }
    } else {
      // Reset form when closing
      setFormData({
        payment_type: defaultType,
        vendor_id: '',
        vendor_name: '',
        customer_id: '',
        customer_name: '',
        amount: '',
        payment_method: 'check',
        reference_number: '',
        payment_date: new Date().toISOString().split('T')[0],
        memo: '',
      });
      setSelectedBills([]);
      setSelectedInvoices([]);
      setVendorSearch('');
      setCustomerSearch('');
      setVendorSearchResults([]);
      setCustomerSearchResults([]);
    }
  }, [open, prefillVendor, prefillBill, prefillCustomer, prefillInvoice, defaultType]);

  // Auto-select prefilled invoice from loaded list (matches by order_id or id)
  // This runs after invoices are loaded to fix the prefilled selection
  useEffect(() => {
    if (open && prefillInvoice?.id && availableInvoices.length > 0) {
      // Check if current selection is valid (exists in available invoices)
      const currentSelectionValid = selectedInvoices.length > 0 && 
        selectedInvoices.some(sel => availableInvoices.some(inv => inv.id === sel.id));
      
      if (!currentSelectionValid) {
        // Find the invoice in the loaded list by matching order_id or id
        const matchingInvoice = availableInvoices.find(inv => 
          inv.id === prefillInvoice.id || 
          inv.order_id === prefillInvoice.id ||
          inv.invoice_number === prefillInvoice.invoice_number
        );
        
        if (matchingInvoice) {
          setSelectedInvoices([{
            id: matchingInvoice.id,
            invoice_number: matchingInvoice.invoice_number,
            total_cents: matchingInvoice.total_cents,
            balance_cents: matchingInvoice.balance_cents,
            apply_amount: (matchingInvoice.balance_cents / 100).toFixed(2),
          }]);
          setFormData(prev => ({
            ...prev,
            amount: (matchingInvoice.balance_cents / 100).toFixed(2),
          }));
        }
      }
    }
  }, [open, prefillInvoice, availableInvoices]);

  // Select vendor
  const handleSelectVendor = (vendor) => {
    setFormData(prev => ({
      ...prev,
      vendor_id: vendor.id,
      vendor_name: vendor.name,
    }));
    setVendorSearch('');
    setVendorSearchResults([]);
    setSelectedBills([]);
  };

  // Select customer
  const handleSelectCustomer = (customer) => {
    setFormData(prev => ({
      ...prev,
      customer_id: customer.id,
      customer_name: customer.name,
    }));
    setCustomerSearch('');
    setCustomerSearchResults([]);
    setSelectedInvoices([]);
  };

  // Toggle bill selection
  const toggleBillSelection = (bill) => {
    const isSelected = selectedBills.find(b => b.id === bill.id);
    if (isSelected) {
      setSelectedBills(prev => prev.filter(b => b.id !== bill.id));
    } else {
      setSelectedBills(prev => [...prev, {
        id: bill.id,
        bill_number: bill.bill_number,
        amount_cents: bill.amount_cents,
        balance_cents: bill.balance_cents,
        apply_amount: (bill.balance_cents / 100).toFixed(2),
      }]);
    }
  };

  // Toggle invoice selection
  const toggleInvoiceSelection = (invoice) => {
    const isSelected = selectedInvoices.find(i => i.id === invoice.id);
    if (isSelected) {
      setSelectedInvoices(prev => prev.filter(i => i.id !== invoice.id));
    } else {
      setSelectedInvoices(prev => [...prev, {
        id: invoice.id,
        invoice_number: invoice.invoice_number,
        total_cents: invoice.total_cents,
        balance_cents: invoice.balance_cents,
        apply_amount: (invoice.balance_cents / 100).toFixed(2),
      }]);
    }
  };

  // Update bill application amount
  const updateBillApplyAmount = (billId, amount) => {
    setSelectedBills(prev => prev.map(b => 
      b.id === billId ? { ...b, apply_amount: amount } : b
    ));
  };

  // Update invoice application amount
  const updateInvoiceApplyAmount = (invoiceId, amount) => {
    setSelectedInvoices(prev => prev.map(i => 
      i.id === invoiceId ? { ...i, apply_amount: amount } : i
    ));
  };

  // Calculate total applied for bills (use Math.round to avoid floating point issues)
  const totalAppliedBills = selectedBills.reduce((sum, b) => sum + Math.round(parseFloat(b.apply_amount || 0) * 100), 0);
  
  // Calculate total applied for invoices (use Math.round to avoid floating point issues)
  const totalAppliedInvoices = selectedInvoices.reduce((sum, i) => sum + Math.round(parseFloat(i.apply_amount || 0) * 100), 0);
  
  // Current total applied based on payment type
  const totalApplied = formData.payment_type === 'expense' ? totalAppliedBills : totalAppliedInvoices;

  // Auto-fill payment amount from selections
  const handleAutoFillAmount = () => {
    const total = formData.payment_type === 'expense' ? totalAppliedBills : totalAppliedInvoices;
    setFormData(prev => ({ ...prev, amount: (total / 100).toFixed(2) }));
  };

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (formData.payment_type === 'expense' && !formData.vendor_id) {
      toast.error('Please select a vendor');
      return;
    }
    
    if (formData.payment_type === 'sales' && !formData.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    
    const amountCents = Math.round(parseFloat(formData.amount) * 100);
    if (!amountCents || amountCents <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    // Validate applications don't exceed payment
    if (totalApplied > amountCents) {
      toast.error('Applied amounts exceed payment amount');
      return;
    }

    setSubmitting(true);
    try {
      // Build applications array based on payment type
      let applications = [];
      
      if (formData.payment_type === 'expense') {
        applications = selectedBills.map(b => ({
          applies_to_type: 'vendor_bill',
          applies_to_id: b.id,
          applied_amount_cents: Math.round(parseFloat(b.apply_amount || 0) * 100),
        })).filter(a => a.applied_amount_cents > 0);
      } else {
        applications = selectedInvoices.map(i => ({
          applies_to_type: 'invoice',
          applies_to_id: i.id,
          applied_amount_cents: Math.round(parseFloat(i.apply_amount || 0) * 100),
        })).filter(a => a.applied_amount_cents > 0);
      }
      
      const payload = {
        payment_type: formData.payment_type,
        vendor_id: formData.payment_type === 'expense' ? formData.vendor_id : null,
        customer_id: formData.payment_type === 'sales' ? formData.customer_id : null,
        amount_cents: amountCents,
        payment_method: formData.payment_method,
        payment_reference: formData.reference_number || null,
        payment_date: formData.payment_date,
        memo: formData.memo || null,
        applications: applications,
      };
      
      const result = await paymentsAPI.createPayment(payload);
      const payment = result.payment || result;
      toast.success(`Payment ${payment.payment_number || payment.id} created`);
      onOpenChange(false);
      onSuccess?.(payment.id);
    } catch (err) {
      toast.error(err.message || 'Failed to create payment');
    } finally {
      setSubmitting(false);
    }
  };

  // Switch payment type
  const handleSwitchPaymentType = (type) => {
    setFormData(prev => ({
      ...prev,
      payment_type: type,
      vendor_id: '',
      vendor_name: '',
      customer_id: '',
      customer_name: '',
    }));
    setSelectedBills([]);
    setSelectedInvoices([]);
    setVendorSearch('');
    setCustomerSearch('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-payment-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-600" />
            Record Payment
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Payment Type */}
          <div className="flex gap-4">
            <Button
              type="button"
              variant={formData.payment_type === 'expense' ? 'default' : 'outline'}
              onClick={() => handleSwitchPaymentType('expense')}
              className="flex-1"
              data-testid="payment-type-expense"
            >
              <Building2 className="w-4 h-4 mr-2" />
              Pay Vendor Bill
            </Button>
            <Button
              type="button"
              variant={formData.payment_type === 'sales' ? 'default' : 'outline'}
              onClick={() => handleSwitchPaymentType('sales')}
              className="flex-1"
              data-testid="payment-type-sales"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Receive Customer Payment
            </Button>
          </div>
          
          {/* Vendor Selection (expense only) */}
          {formData.payment_type === 'expense' && (
            <div className="space-y-2">
              <Label>Vendor <span className="text-red-500">*</span></Label>
              {formData.vendor_id ? (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-gray-400" />
                    <span className="font-medium">{formData.vendor_name}</span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setFormData(prev => ({ ...prev, vendor_id: '', vendor_name: '' }));
                      setSelectedBills([]);
                    }}
                  >
                    Change
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Input
                    placeholder="Search vendors..."
                    value={vendorSearch}
                    onChange={(e) => setVendorSearch(e.target.value)}
                    data-testid="vendor-search-input"
                  />
                  {vendorSearchResults.length > 0 && (
                    <div className="max-h-40 overflow-y-auto border rounded-lg">
                      {vendorSearchResults.map(v => (
                        <button
                          key={v.id}
                          type="button"
                          className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2"
                          onClick={() => handleSelectVendor(v)}
                        >
                          <Building2 className="w-4 h-4 text-gray-400" />
                          {v.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Customer Selection (sales only) */}
          {formData.payment_type === 'sales' && (
            <div className="space-y-2">
              <Label>Customer <span className="text-red-500">*</span></Label>
              {formData.customer_id ? (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-400" />
                    <span className="font-medium">{formData.customer_name}</span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setFormData(prev => ({ ...prev, customer_id: '', customer_name: '' }));
                      setSelectedInvoices([]);
                    }}
                  >
                    Change
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Input
                    placeholder="Search customers..."
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    data-testid="customer-search-input"
                  />
                  {customerSearchResults.length > 0 && (
                    <div className="max-h-40 overflow-y-auto border rounded-lg">
                      {customerSearchResults.map(c => (
                        <button
                          key={c.id}
                          type="button"
                          className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2"
                          onClick={() => handleSelectCustomer(c)}
                        >
                          <User className="w-4 h-4 text-gray-400" />
                          {c.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Payment Details */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="amount">Amount <span className="text-red-500">*</span></Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  placeholder="0.00"
                  value={formData.amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                  className="pl-7"
                  data-testid="payment-amount-input"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="payment_method">Payment Method</Label>
              <select
                id="payment_method"
                value={formData.payment_method}
                onChange={(e) => setFormData(prev => ({ ...prev, payment_method: e.target.value }))}
                className="w-full h-10 border rounded-md px-3"
                data-testid="payment-method-select"
              >
                {PAYMENT_METHODS.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <Label htmlFor="payment_date">Payment Date</Label>
              <Input
                id="payment_date"
                type="date"
                value={formData.payment_date}
                onChange={(e) => setFormData(prev => ({ ...prev, payment_date: e.target.value }))}
                data-testid="payment-date-input"
              />
            </div>
            
            <div>
              <Label htmlFor="reference_number">Reference / Check #</Label>
              <Input
                id="reference_number"
                placeholder="e.g., CHK-1234"
                value={formData.reference_number}
                onChange={(e) => setFormData(prev => ({ ...prev, reference_number: e.target.value }))}
                data-testid="payment-reference-input"
              />
            </div>
          </div>
          
          {/* Bill Selection (expense only) */}
          {formData.payment_type === 'expense' && formData.vendor_id && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Apply to Bills</Label>
                <div className="flex items-center gap-2">
                  {selectedBills.length > 0 && (
                    <>
                      <span className="text-sm text-gray-500">
                        Applied: {formatCurrency(totalAppliedBills)} of {formatCurrency(Math.round(parseFloat(formData.amount || 0) * 100))}
                      </span>
                      <Button type="button" variant="ghost" size="sm" onClick={handleAutoFillAmount}>
                        Auto-fill Amount
                      </Button>
                    </>
                  )}
                </div>
              </div>
              
              {loadingBills ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              ) : availableBills.length > 0 ? (
                <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                  {availableBills.map(bill => {
                    const isSelected = selectedBills.find(b => b.id === bill.id);
                    return (
                      <div 
                        key={bill.id} 
                        className={`p-3 flex items-center justify-between ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={!!isSelected}
                            onChange={() => toggleBillSelection(bill)}
                            className="rounded"
                          />
                          <div>
                            <div className="font-medium text-sm">{bill.bill_number || bill.id.slice(0, 12)}</div>
                            <div className="text-xs text-gray-500">
                              Balance: {formatCurrency(bill.balance_cents)}
                            </div>
                          </div>
                        </div>
                        {isSelected && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">Apply:</span>
                            <Input
                              type="number"
                              step="0.01"
                              min="0.01"
                              max={bill.balance_cents / 100}
                              value={isSelected.apply_amount}
                              onChange={(e) => updateBillApplyAmount(bill.id, e.target.value)}
                              className="w-24 h-8 text-sm"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 text-sm">
                  <FileText className="w-6 h-6 mx-auto mb-1 opacity-50" />
                  No unpaid bills for this vendor
                </div>
              )}
            </div>
          )}
          
          {/* Invoice Selection (sales only) */}
          {formData.payment_type === 'sales' && formData.customer_id && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Apply to Invoices</Label>
                <div className="flex items-center gap-2">
                  {selectedInvoices.length > 0 && (
                    <>
                      <span className="text-sm text-gray-500">
                        Applied: {formatCurrency(totalAppliedInvoices)} of {formatCurrency(Math.round(parseFloat(formData.amount || 0) * 100))}
                      </span>
                      <Button type="button" variant="ghost" size="sm" onClick={handleAutoFillAmount}>
                        Auto-fill Amount
                      </Button>
                    </>
                  )}
                </div>
              </div>
              
              {loadingInvoices ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              ) : availableInvoices.length > 0 ? (
                <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                  {availableInvoices.map(invoice => {
                    const isSelected = selectedInvoices.find(i => i.id === invoice.id);
                    return (
                      <div 
                        key={invoice.id} 
                        className={`p-3 flex items-center justify-between ${isSelected ? 'bg-green-50' : 'hover:bg-gray-50'}`}
                        data-testid={`invoice-row-${invoice.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={!!isSelected}
                            onChange={() => toggleInvoiceSelection(invoice)}
                            className="rounded"
                          />
                          <div>
                            <div className="font-medium text-sm">{invoice.invoice_number || invoice.order_number}</div>
                            <div className="text-xs text-gray-500">
                              Balance: {formatCurrency(invoice.balance_cents)}
                            </div>
                          </div>
                        </div>
                        {isSelected && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">Apply:</span>
                            <Input
                              type="number"
                              step="0.01"
                              min="0.01"
                              max={invoice.balance_cents / 100}
                              value={isSelected.apply_amount}
                              onChange={(e) => updateInvoiceApplyAmount(invoice.id, e.target.value)}
                              className="w-24 h-8 text-sm"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 text-sm">
                  <FileText className="w-6 h-6 mx-auto mb-1 opacity-50" />
                  No open invoices for this customer
                </div>
              )}
            </div>
          )}
          
          {/* Memo */}
          <div>
            <Label htmlFor="memo">Memo</Label>
            <Textarea
              id="memo"
              placeholder="Optional notes..."
              value={formData.memo}
              onChange={(e) => setFormData(prev => ({ ...prev, memo: e.target.value }))}
              rows={2}
              data-testid="payment-memo-input"
            />
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={submitting}
              className="bg-green-600 hover:bg-green-700"
              data-testid="create-payment-submit-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Record Payment
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreatePaymentModal;
